import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../api/axios';

interface Props {
  open: boolean;
  onClose: () => void;
  preselectedRuleIds: string[];
  /** Called with the new scan_id on success — caller typically navigates to it. */
  onLaunched: (scanId: string) => void;
}

const LaunchScanDialog = ({ open, onClose, preselectedRuleIds, onLaunched }: Props) => {
  const [allowBulk, setAllowBulk] = useState(false);
  const [scopeText, setScopeText] = useState('{}');
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLaunch = async () => {
    setError(null);
    let scope: Record<string, any> = {};
    try {
      scope = scopeText.trim() ? JSON.parse(scopeText) : {};
    } catch (e: any) {
      setError(`Invalid scope JSON: ${e.message}`);
      return;
    }
    setLaunching(true);
    try {
      // Launching from a rule always writes a production scan. For exploratory
      // dry-runs against parameter overrides, use the Iterate tab's test_run.
      const res = await api.post('/explore/scans/', {
        rule_ids: preselectedRuleIds,
        scan_type: 'production',
        scope,
        allow_bulk: allowBulk,
      });
      onLaunched(res.data.scan_id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'failed');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Launch Scan</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Rules ({preselectedRuleIds.length})</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {preselectedRuleIds.map((id) => (
                <Typography
                  key={id}
                  variant="caption"
                  sx={{
                    px: 1, py: 0.25, borderRadius: 0.5,
                    backgroundColor: 'action.hover', fontFamily: 'monospace',
                  }}
                >
                  {id}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Scope (optional JSON)</Typography>
            <TextField
              multiline
              fullWidth
              minRows={2}
              value={scopeText}
              onChange={(e) => setScopeText(e.target.value)}
              placeholder='{"date_range": ["2024-01", "2024-12"]}'
              sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
          </Box>

          <FormControlLabel
            control={<Checkbox checked={allowBulk} onChange={(e) => setAllowBulk(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2">Allow bulk override</Typography>
                <Typography variant="caption" color="text.secondary">
                  Override the per-rule max_fires_per_scan cap (default 1000). Only check this if you intentionally want a wide net.
                </Typography>
              </Box>
            }
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={launching} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleLaunch}
          disabled={launching || preselectedRuleIds.length === 0}
          startIcon={launching ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {launching ? 'Launching…' : 'Launch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LaunchScanDialog;
